<script context="module">
  import { External, M } from "../external";
  import ImageEditor from "./ImageEditor.svelte";

  // options: { imageUrl, fileName, onSave(blob, suggestedName) -> Promise }
  export function openImageEditor(options) {
    External.Tools.showWindow({
      noTitle: true,
      width: 1180,
      height: "86%",
      isModal: true,
      withDarkBackground: true,
      overflowY: "hidden",
      overflowX: "hidden",
      buttons: [],
      customClass: "cpanel-image-editor",
      action: {
        onShow: (popup, context) => {
          if (context && context.css) context.css("overflow", "hidden");
          popup.bindComponent(
            new ImageEditor({
              target: popup.getContentArea(),
              props: {
                imageUrl: options.imageUrl,
                fileName: options.fileName,
                onSave: options.onSave,
                onClose: () => popup.close()
              }
            })
          );
        }
      }
    });
  }
</script>

<script>
  import { onMount, tick } from "svelte";
  import {
    ADJUSTMENT_DEFAULTS,
    FILTER_PRESETS,
    buildOps,
    cssFilterForOps,
    drawOriented,
    exportBlob,
    exportFormatFor,
    flipRectH,
    flipRectV,
    loadImage,
    rotateRectCCW,
    rotateRectCW
  } from "./engine";

  export let imageUrl = "";
  export let fileName = "image.jpg";
  export let onSave = () => Promise.resolve();
  export let onClose = () => {};

  const T = {
    editImage: M.IMAGE_EDITOR_TITLE || "Edit image",
    crop: M.IMAGE_EDITOR_CROP || "Crop",
    removeCrop: M.IMAGE_EDITOR_REMOVE_CROP || "Remove crop",
    apply: M.APPLY || "Apply",
    cancel: M.CANCEL || "Cancel",
    adjust: M.IMAGE_EDITOR_ADJUST || "Adjust",
    brightness: M.IMAGE_EDITOR_BRIGHTNESS || "Brightness",
    contrast: M.IMAGE_EDITOR_CONTRAST || "Contrast",
    saturation: M.IMAGE_EDITOR_SATURATION || "Saturation",
    filters: M.IMAGE_EDITOR_FILTERS || "Filters",
    size: M.IMAGE_EDITOR_SIZE || "Output size",
    reset: M.IMAGE_EDITOR_RESET || "Reset all",
    save: M.IMAGE_EDITOR_SAVE_COPY || "Save as copy",
    saving: M.SAVING || "Saving...",
    loadError: M.IMAGE_EDITOR_LOAD_ERROR || "The image could not be loaded.",
    retry: M.TRY_AGAIN || "Try again",
    rotateLeft: M.IMAGE_EDITOR_ROTATE_LEFT || "Rotate left",
    rotateRight: M.IMAGE_EDITOR_ROTATE_RIGHT || "Rotate right",
    flipH: M.IMAGE_EDITOR_FLIP_H || "Flip horizontal",
    flipV: M.IMAGE_EDITOR_FLIP_V || "Flip vertical",
    free: M.IMAGE_EDITOR_FREE || "Free",
    presetNames: {
      none: M.NONE || "None",
      bw: M.IMAGE_EDITOR_PRESET_BW || "B&W",
      sepia: M.IMAGE_EDITOR_PRESET_SEPIA || "Sepia",
      vivid: M.IMAGE_EDITOR_PRESET_VIVID || "Vivid",
      warm: M.IMAGE_EDITOR_PRESET_WARM || "Warm",
      cool: M.IMAGE_EDITOR_PRESET_COOL || "Cool",
      soft: M.IMAGE_EDITOR_PRESET_SOFT || "Soft"
    }
  };

  const ASPECTS = [
    { label: T.free, value: null },
    { label: "1:1", value: 1 },
    { label: "4:3", value: 4 / 3 },
    { label: "3:4", value: 3 / 4 },
    { label: "16:9", value: 16 / 9 }
  ];

  const MIN_CROP = 16;

  let img = null;
  let loading = true;
  let loadError = false;
  let saving = false;
  let previewThumb = null;

  let rotation = 0;
  let flipH = false;
  let flipV = false;
  let crop = null; // {x,y,w,h} in oriented-image space, null = full image
  let adjustments = { ...ADJUSTMENT_DEFAULTS };
  let preset = "none";

  let cropMode = false;
  let pendingCrop = null;
  let cropAspect = null;

  let outWidth = 0;
  let outHeight = 0;
  let userResized = false;

  let areaEl = null;
  let canvasEl = null;
  let viewRect = null; // displayed canvas rect relative to areaEl
  let drag = null;

  $: orientedCanvas = img ? drawOriented(img, rotation, flipH, flipV) : null;
  $: orientedW = orientedCanvas ? orientedCanvas.width : 0;
  $: orientedH = orientedCanvas ? orientedCanvas.height : 0;

  $: cropW = crop ? crop.w : orientedW;
  $: cropH = crop ? crop.h : orientedH;
  $: if (!userResized && cropW && cropH) {
    outWidth = Math.round(cropW);
    outHeight = Math.round(cropH);
  }

  $: previewFilter = cssFilterForOps(buildOps(adjustments, preset));
  $: viewScale = viewRect && canvasEl && canvasEl.width ? viewRect.width / canvasEl.width : 1;
  $: renderPreview(orientedCanvas, crop, cropMode, canvasEl);

  function renderPreview() {
    if (!orientedCanvas || !canvasEl) return;
    const rect = cropMode || !crop ? { x: 0, y: 0, w: orientedW, h: orientedH } : crop;
    canvasEl.width = Math.max(1, Math.round(rect.w));
    canvasEl.height = Math.max(1, Math.round(rect.h));
    const ctx = canvasEl.getContext("2d");
    ctx.drawImage(orientedCanvas, rect.x, rect.y, rect.w, rect.h, 0, 0, canvasEl.width, canvasEl.height);
    tick().then(measureView);
  }

  function measureView() {
    if (!canvasEl || !areaEl) return;
    const c = canvasEl.getBoundingClientRect();
    const a = areaEl.getBoundingClientRect();
    viewRect = { left: c.left - a.left, top: c.top - a.top, width: c.width, height: c.height };
  }

  function makePreviewThumb(image) {
    const tw = 120, th = 80;
    const c = document.createElement("canvas");
    c.width = tw;
    c.height = th;
    const ctx = c.getContext("2d");
    const scale = Math.max(tw / image.width, th / image.height);
    const dw = image.width * scale, dh = image.height * scale;
    ctx.drawImage(image, (tw - dw) / 2, (th - dh) / 2, dw, dh);
    return c.toDataURL("image/jpeg", 0.72);
  }

  // --- Transform ---

  function rotate(dir) {
    const w = orientedW, h = orientedH;
    const remap = dir > 0 ? rotateRectCW : rotateRectCCW;
    if (crop) crop = remap(crop, w, h);
    if (pendingCrop) pendingCrop = remap(pendingCrop, w, h);
    if (cropAspect) cropAspect = 1 / cropAspect;
    rotation = (rotation + (dir > 0 ? 90 : 270)) % 360;
  }

  // Flips are expressed in view space; when the image is rotated 90/270 the
  // underlying pre-rotation flag for the other axis must be toggled instead.
  function flip(axis) {
    if (axis === "h") {
      if (crop) crop = flipRectH(crop, orientedW);
      if (pendingCrop) pendingCrop = flipRectH(pendingCrop, orientedW);
      if (rotation % 180 !== 0) flipV = !flipV;
      else flipH = !flipH;
    } else {
      if (crop) crop = flipRectV(crop, orientedH);
      if (pendingCrop) pendingCrop = flipRectV(pendingCrop, orientedH);
      if (rotation % 180 !== 0) flipH = !flipH;
      else flipV = !flipV;
    }
  }

  // --- Crop ---

  function enterCropMode() {
    pendingCrop = crop ? { ...crop } : { x: 0, y: 0, w: orientedW, h: orientedH };
    cropAspect = null;
    cropMode = true;
  }

  function applyCrop() {
    const r = clampRect(roundRect(pendingCrop), orientedW, orientedH);
    crop = r.x === 0 && r.y === 0 && r.w === orientedW && r.h === orientedH ? null : r;
    cropMode = false;
  }

  function cancelCrop() {
    cropMode = false;
  }

  function removeCrop() {
    crop = null;
  }

  function roundRect(r) {
    const x = Math.round(r.x), y = Math.round(r.y);
    return { x: x, y: y, w: Math.round(r.w), h: Math.round(r.h) };
  }

  function clampRect(r, w, h) {
    const rw = Math.min(r.w, w), rh = Math.min(r.h, h);
    return {
      x: Math.min(Math.max(r.x, 0), w - rw),
      y: Math.min(Math.max(r.y, 0), h - rh),
      w: rw,
      h: rh
    };
  }

  function setAspect(value) {
    cropAspect = value;
    if (!value || !pendingCrop) return;
    let w = pendingCrop.w;
    let h = w / value;
    if (h > pendingCrop.h) {
      h = pendingCrop.h;
      w = h * value;
    }
    if (w > orientedW) {
      w = orientedW;
      h = w / value;
    }
    if (h > orientedH) {
      h = orientedH;
      w = h * value;
    }
    const cx = pendingCrop.x + pendingCrop.w / 2;
    const cy = pendingCrop.y + pendingCrop.h / 2;
    pendingCrop = clampRect({ x: cx - w / 2, y: cy - h / 2, w: w, h: h }, orientedW, orientedH);
  }

  function startDrag(event, mode) {
    if (event.button !== undefined && event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    drag = { mode: mode, px: event.clientX, py: event.clientY, rect: { ...pendingCrop } };
    window.addEventListener("pointermove", onDragMove);
    window.addEventListener("pointerup", endDrag);
  }

  function onDragMove(event) {
    if (!drag || !viewScale) return;
    const dx = (event.clientX - drag.px) / viewScale;
    const dy = (event.clientY - drag.py) / viewScale;
    const r0 = drag.rect;
    const min = Math.min(MIN_CROP, orientedW, orientedH);

    if (drag.mode === "move") {
      pendingCrop = {
        x: Math.min(Math.max(r0.x + dx, 0), orientedW - r0.w),
        y: Math.min(Math.max(r0.y + dy, 0), orientedH - r0.h),
        w: r0.w,
        h: r0.h
      };
      return;
    }

    if (cropAspect) {
      pendingCrop = resizeWithAspect(r0, drag.mode, dx, dy, cropAspect, min);
    } else {
      pendingCrop = resizeFree(r0, drag.mode, dx, dy, min);
    }
  }

  function resizeFree(r0, handle, dx, dy, min) {
    let x = r0.x, y = r0.y, w = r0.w, h = r0.h;
    if (handle.includes("w")) {
      const nx = Math.min(Math.max(r0.x + dx, 0), r0.x + r0.w - min);
      w = r0.x + r0.w - nx;
      x = nx;
    } else if (handle.includes("e")) {
      w = Math.min(Math.max(r0.w + dx, min), orientedW - r0.x);
    }
    if (handle.includes("n")) {
      const ny = Math.min(Math.max(r0.y + dy, 0), r0.y + r0.h - min);
      h = r0.y + r0.h - ny;
      y = ny;
    } else if (handle.includes("s")) {
      h = Math.min(Math.max(r0.h + dy, min), orientedH - r0.y);
    }
    return { x: x, y: y, w: w, h: h };
  }

  // Corner resize with a locked ratio, anchored at the opposite corner.
  function resizeWithAspect(r0, handle, dx, dy, ratio, min) {
    const ax = handle.includes("w") ? r0.x + r0.w : r0.x;
    const ay = handle.includes("n") ? r0.y + r0.h : r0.y;
    const sx = handle.includes("w") ? -1 : 1;
    const sy = handle.includes("n") ? -1 : 1;
    let w = Math.max(r0.w + sx * dx, (r0.h + sy * dy) * ratio);
    const maxW = Math.min(sx > 0 ? orientedW - ax : ax, (sy > 0 ? orientedH - ay : ay) * ratio);
    w = Math.min(Math.max(w, Math.max(min, min * ratio)), maxW);
    const h = w / ratio;
    return {
      x: sx > 0 ? ax : ax - w,
      y: sy > 0 ? ay : ay - h,
      w: w,
      h: h
    };
  }

  function endDrag() {
    drag = null;
    window.removeEventListener("pointermove", onDragMove);
    window.removeEventListener("pointerup", endDrag);
  }

  $: cropHandles = cropAspect ? ["nw", "ne", "se", "sw"] : ["nw", "n", "ne", "e", "se", "s", "sw", "w"];

  function cropBoxStyle(rect, view, scale) {
    return (
      "left:" + (view.left + rect.x * scale) + "px;" +
      "top:" + (view.top + rect.y * scale) + "px;" +
      "width:" + (rect.w * scale) + "px;" +
      "height:" + (rect.h * scale) + "px;"
    );
  }

  // --- Output size ---

  function onWidthInput(event) {
    const v = parseInt(event.target.value, 10);
    if (!v || v < 1 || !cropW) return;
    const max = Math.min(8192, Math.round(cropW * 2));
    outWidth = Math.min(v, max);
    outHeight = Math.max(1, Math.round(outWidth * (cropH / cropW)));
    userResized = true;
  }

  function onHeightInput(event) {
    const v = parseInt(event.target.value, 10);
    if (!v || v < 1 || !cropH) return;
    const max = Math.min(8192, Math.round(cropH * 2));
    outHeight = Math.min(v, max);
    outWidth = Math.max(1, Math.round(outHeight * (cropW / cropH)));
    userResized = true;
  }

  function sliderStyle(value, min, max) {
    const pct = ((value - min) / (max - min)) * 100;
    return "--fill:" + pct + "%";
  }

  // --- Reset / save ---

  function resetAll() {
    rotation = 0;
    flipH = false;
    flipV = false;
    crop = null;
    cropMode = false;
    pendingCrop = null;
    cropAspect = null;
    adjustments = { ...ADJUSTMENT_DEFAULTS };
    preset = "none";
    userResized = false;
  }

  async function save() {
    if (saving || !img) return;
    // Commit a crop still being adjusted so what's on screen is what gets saved
    if (cropMode && pendingCrop) applyCrop();
    // Compute output dimensions here instead of using the reactive outWidth/outHeight:
    // reactive statements haven't re-run yet after applyCrop, so those can be stale.
    const cw = crop ? crop.w : orientedW;
    const ch = crop ? crop.h : orientedH;
    let exportW = Math.max(1, Math.round(cw));
    let exportH = Math.max(1, Math.round(ch));
    if (userResized && outWidth > 0) {
      exportW = outWidth;
      exportH = Math.max(1, Math.round(exportW * (ch / cw)));
    }
    saving = true;
    try {
      const format = exportFormatFor(fileName);
      const blob = await exportBlob({
        image: img,
        rotation: rotation,
        flipH: flipH,
        flipV: flipV,
        crop: crop,
        outWidth: exportW,
        outHeight: exportH,
        ops: buildOps(adjustments, preset),
        mimeType: format.mimeType,
        quality: format.quality
      });
      const base = fileName.replace(/\.[^.]+$/, "");
      await onSave(blob, base + "-edited." + format.extension);
      onClose();
    } catch (e) {
      External.Log.error(e);
      External.CPanel.showError(M.ERR_UPS);
    } finally {
      saving = false;
    }
  }

  function initLoad() {
    loading = true;
    loadError = false;
    loadImage(imageUrl)
      .then((loaded) => {
        img = loaded;
        previewThumb = makePreviewThumb(loaded);
        loading = false;
      })
      .catch(() => {
        loading = false;
        loadError = true;
      });
  }

  onMount(initLoad);
</script>

<svelte:window on:resize={measureView} />

<div class="ie-root">
  <div class="ie-stage" bind:this={areaEl}>
    {#if loading}
      <div class="ie-status">
        <div class="ie-spinner" />
      </div>
    {:else if loadError}
      <div class="ie-status">
        <span class="ie-error-text">{T.loadError}</span>
        <button class="ie-chip" on:click={initLoad}>{T.retry}</button>
      </div>
    {:else}
      <canvas bind:this={canvasEl} style="filter: {previewFilter};" />
      {#if cropMode && pendingCrop && viewRect}
        <div
          class="ie-crop"
          style={cropBoxStyle(pendingCrop, viewRect, viewScale)}
          on:pointerdown={(e) => startDrag(e, "move")}>
          {#each cropHandles as handle}
            <div class="ie-h ie-h-{handle}" on:pointerdown={(e) => startDrag(e, handle)} />
          {/each}
        </div>
      {/if}
    {/if}

    {#if !loading && !loadError}
      <div class="ie-toolbar">
        {#if !cropMode}
          <button class="ie-tool" title={T.rotateLeft} on:click={() => rotate(-1)} disabled={saving}>
            <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8v5h5"/><path d="M3.3 13a9 9 0 1 0 2-7L3 8"/></svg>
          </button>
          <button class="ie-tool" title={T.rotateRight} on:click={() => rotate(1)} disabled={saving}>
            <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M21 8v5h-5"/><path d="M20.7 13a9 9 0 1 1-2-7L21 8"/></svg>
          </button>
          <span class="ie-sep" />
          <button class="ie-tool" title={T.flipH} on:click={() => flip("h")} disabled={saving}>
            <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v18"/><path d="M8 7 4 12l4 5"/><path d="m16 7 4 5-4 5"/></svg>
          </button>
          <button class="ie-tool" title={T.flipV} on:click={() => flip("v")} disabled={saving}>
            <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12h18"/><path d="M7 8 12 4l5 4"/><path d="M7 16l5 4 5-4"/></svg>
          </button>
          <span class="ie-sep" />
          <button class="ie-tool ie-tool-label" on:click={enterCropMode} disabled={saving}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M6 2v14a2 2 0 0 0 2 2h14"/><path d="M18 22V8a2 2 0 0 0-2-2H2"/></svg>
            <span>{T.crop}</span>
          </button>
          {#if crop}
            <button class="ie-tool ie-tool-label" on:click={removeCrop} disabled={saving}>
              <span>{T.removeCrop}</span>
            </button>
          {/if}
        {:else}
          {#each ASPECTS as aspect}
            <button
              class="ie-chip"
              class:active={cropAspect === aspect.value}
              on:click={() => setAspect(aspect.value)}>{aspect.label}</button>
          {/each}
          <span class="ie-sep" />
          <button class="ie-chip ie-chip-accent" on:click={applyCrop}>{T.apply}</button>
          <button class="ie-tool" title={T.cancel} on:click={cancelCrop}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        {/if}
      </div>
    {/if}
  </div>

  <aside class="ie-rail">
    <header class="ie-head">
      <div class="ie-head-text">
        <span class="ie-eyebrow">{T.editImage}</span>
        <span class="ie-name" title={fileName}>{fileName}</span>
        <span class="ie-dims">{loading || loadError ? "—" : orientedW + " × " + orientedH + " px"}</span>
      </div>
      <button class="ie-close" title={T.cancel} on:click={onClose}>
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
      </button>
    </header>

    <div class="ie-body" class:ie-disabled={loading || loadError}>
      <section class="ie-section">
        <h3 class="ie-label">{T.adjust}</h3>
        <label class="ie-slider">
          <span class="ie-slider-row"><span>{T.brightness}</span><em>{adjustments.brightness}</em></span>
          <input
            type="range"
            min="40"
            max="160"
            bind:value={adjustments.brightness}
            style={sliderStyle(adjustments.brightness, 40, 160)}
            disabled={saving} />
        </label>
        <label class="ie-slider">
          <span class="ie-slider-row"><span>{T.contrast}</span><em>{adjustments.contrast}</em></span>
          <input
            type="range"
            min="40"
            max="160"
            bind:value={adjustments.contrast}
            style={sliderStyle(adjustments.contrast, 40, 160)}
            disabled={saving} />
        </label>
        <label class="ie-slider">
          <span class="ie-slider-row"><span>{T.saturation}</span><em>{adjustments.saturation}</em></span>
          <input
            type="range"
            min="0"
            max="200"
            bind:value={adjustments.saturation}
            style={sliderStyle(adjustments.saturation, 0, 200)}
            disabled={saving} />
        </label>
      </section>

      <section class="ie-section">
        <h3 class="ie-label">{T.filters}</h3>
        <div class="ie-filters">
          {#each FILTER_PRESETS as filterPreset}
            <button
              class="ie-filter"
              class:active={preset === filterPreset.id}
              on:click={() => (preset = filterPreset.id)}
              disabled={saving}>
              {#if previewThumb}
                <img src={previewThumb} alt="" style="filter: {cssFilterForOps(filterPreset.ops)};" />
              {:else}
                <span class="ie-filter-blank" />
              {/if}
              <span class="ie-filter-name">{T.presetNames[filterPreset.id]}</span>
            </button>
          {/each}
        </div>
      </section>

      <section class="ie-section">
        <h3 class="ie-label">{T.size}</h3>
        <div class="ie-size">
          <input type="number" min="1" value={outWidth} on:change={onWidthInput} disabled={saving} />
          <span class="ie-size-x">×</span>
          <input type="number" min="1" value={outHeight} on:change={onHeightInput} disabled={saving} />
          <span class="ie-size-unit">px</span>
        </div>
      </section>
    </div>

    <footer class="ie-foot">
      <button class="ie-save" class:ie-busy={saving} disabled={saving || loading || loadError} on:click={save}>
        {saving ? T.saving : T.save}
      </button>
      <div class="ie-foot-row">
        <button class="ie-quiet" on:click={resetAll} disabled={saving}>{T.reset}</button>
        <button class="ie-quiet" on:click={onClose} disabled={saving}>{T.cancel}</button>
      </div>
    </footer>
  </aside>
</div>

<style>
  .ie-root {
    --ie-bg: #131418;
    --ie-panel: #1d1f25;
    --ie-line: rgba(255, 255, 255, 0.08);
    --ie-text: #edeef1;
    --ie-dim: #8d93a0;
    --ie-accent: #4eb3ea;
    --ie-accent-ink: #0a1520;

    display: flex;
    width: 100%;
    height: 100%;
    min-height: 460px;
    background: var(--ie-bg);
    color: var(--ie-text);
    text-align: left;
    font-size: 13px;
    line-height: 1.4;
  }

  .ie-root :global(*),
  .ie-root :global(*:before),
  .ie-root :global(*:after) {
    box-sizing: border-box;
  }

  /* Bare element selector on purpose: it must lose to every .ie-* class rule */
  button {
    font: inherit;
    color: inherit;
    background: none;
    border: 0;
    padding: 0;
    cursor: pointer;
  }

  button:disabled {
    opacity: 0.45;
    cursor: default;
  }

  

  /* ---- Stage ---- */

  .ie-stage {
    position: relative;
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    padding: 28px 28px 88px;
  }

  .ie-stage canvas {
    max-width: 100%;
    max-height: 100%;
    display: block;
    border-radius: 4px;
    /* dark checkerboard shows through transparent pixels */
    background:
      conic-gradient(#1c1e23 90deg, #23252c 0 180deg, #1c1e23 0 270deg, #23252c 0) 0 0 / 16px 16px;
    box-shadow: 0 8px 40px rgba(0, 0, 0, 0.45);
  }

  .ie-status {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    color: var(--ie-dim);
  }

  .ie-error-text {
    font-size: 13px;
  }

  

  @keyframes ie-spin {
    to {
      transform: rotate(360deg);
    }
  }

  /* ---- Floating toolbar ---- */

  .ie-toolbar {
    position: absolute;
    left: 50%;
    bottom: 22px;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 6px;
    border-radius: 12px;
    background: rgba(26, 28, 33, 0.86);
    border: 1px solid var(--ie-line);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);
    backdrop-filter: blur(14px) saturate(1.3);
    -webkit-backdrop-filter: blur(14px) saturate(1.3);
    white-space: nowrap;
    z-index: 3;
  }

  .ie-tool {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    min-width: 34px;
    height: 34px;
    padding: 0 8px;
    border-radius: 8px;
    color: #c6cad2;
  }

  .ie-tool:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.07);
    color: #fff;
  }

  .ie-tool-label {
    padding: 0 12px;
    font-size: 12.5px;
  }

  .ie-sep {
    width: 1px;
    height: 20px;
    margin: 0 5px;
    background: var(--ie-line);
  }

  .ie-chip {
    height: 30px;
    padding: 0 12px;
    border-radius: 8px;
    font-size: 12.5px;
    color: #c6cad2;
  }

  .ie-chip:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.07);
    color: #fff;
  }

  

  /* ---- Crop overlay ---- */

  .ie-crop {
    position: absolute;
    border: 1px solid rgba(255, 255, 255, 0.9);
    box-shadow: 0 0 0 100000px rgba(8, 9, 12, 0.62);
    cursor: move;
    touch-action: none;
    z-index: 2;
  }

  .ie-crop:before,
  .ie-crop:after {
    content: "";
    position: absolute;
    pointer-events: none;
    border: 0 solid rgba(255, 255, 255, 0.22);
  }

  .ie-crop:before {
    left: 33.33%;
    right: 33.33%;
    top: 0;
    bottom: 0;
    border-left-width: 1px;
    border-right-width: 1px;
  }

  .ie-crop:after {
    top: 33.33%;
    bottom: 33.33%;
    left: 0;
    right: 0;
    border-top-width: 1px;
    border-bottom-width: 1px;
  }

  .ie-h {
    position: absolute;
    width: 20px;
    height: 20px;
    touch-action: none;
  }

  /* corner handles drawn as L-brackets */
  .ie-h-nw, .ie-h-ne, .ie-h-se, .ie-h-sw {
    border: 0 solid #fff;
  }

  .ie-h-nw { left: -3px; top: -3px; border-left-width: 3px; border-top-width: 3px; cursor: nwse-resize; }
  .ie-h-ne { right: -3px; top: -3px; border-right-width: 3px; border-top-width: 3px; cursor: nesw-resize; }
  .ie-h-se { right: -3px; bottom: -3px; border-right-width: 3px; border-bottom-width: 3px; cursor: nwse-resize; }
  .ie-h-sw { left: -3px; bottom: -3px; border-left-width: 3px; border-bottom-width: 3px; cursor: nesw-resize; }

  /* edge handles: slim pills */
  .ie-h-n, .ie-h-s {
    left: 50%;
    width: 36px;
    height: 14px;
    margin-left: -18px;
  }

  .ie-h-e, .ie-h-w {
    top: 50%;
    width: 14px;
    height: 36px;
    margin-top: -18px;
  }

  .ie-h-n { top: -7px; cursor: ns-resize; }
  .ie-h-s { bottom: -7px; cursor: ns-resize; }
  .ie-h-e { right: -7px; cursor: ew-resize; }
  .ie-h-w { left: -7px; cursor: ew-resize; }

  .ie-h-n:after, .ie-h-s:after, .ie-h-e:after, .ie-h-w:after {
    content: "";
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    background: #fff;
    border-radius: 3px;
  }

  .ie-h-n:after, .ie-h-s:after { width: 22px; height: 4px; }
  .ie-h-e:after, .ie-h-w:after { width: 4px; height: 22px; }

  /* ---- Rail ---- */

  .ie-rail {
    width: 296px;
    min-width: 296px;
    display: flex;
    flex-direction: column;
    background: var(--ie-panel);
    border-left: 1px solid var(--ie-line);
  }

  .ie-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    padding: 18px 18px 16px 20px;
    border-bottom: 1px solid var(--ie-line);
  }

  .ie-head-text {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
  }

  .ie-name {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--ie-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ie-dims {
    font-size: 11.5px;
    color: var(--ie-dim);
    font-variant-numeric: tabular-nums;
  }

  .ie-close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    color: var(--ie-dim);
    flex-shrink: 0;
  }

  .ie-close:hover {
    background: rgba(255, 255, 255, 0.07);
    color: #fff;
  }

  .ie-body {
    flex: 1;
    overflow-y: auto;
    padding: 6px 20px 12px;
  }

  .ie-disabled {
    opacity: 0.4;
    pointer-events: none;
  }

  .ie-section {
    padding: 16px 0;
  }

  .ie-section + .ie-section {
    border-top: 1px solid var(--ie-line);
  }

  .ie-label {
    margin: 0 0 12px;
    font-size: 10.5px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.9px;
    color: var(--ie-dim);
  }

  /* sliders */

  .ie-slider {
    display: block;
    margin-bottom: 14px;
  }

  .ie-slider:last-child {
    margin-bottom: 2px;
  }

  .ie-slider-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 7px;
    font-size: 12.5px;
    color: #c6cad2;
  }

  .ie-slider-row em {
    font-style: normal;
    font-size: 11.5px;
    color: var(--ie-dim);
    font-variant-numeric: tabular-nums;
  }

  .ie-slider input[type="range"] {
    -webkit-appearance: none;
    appearance: none;
    display: block;
    width: 100%;
    height: 16px;
    margin: 0;
    background: transparent;
  }

  

  .ie-slider input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 13px;
    height: 13px;
    margin-top: -5px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
  }

  

  .ie-slider input[type="range"]::-moz-range-thumb {
    width: 13px;
    height: 13px;
    border: 0;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.5);
  }

  /* filters */

  .ie-filters {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
  }

  .ie-filter {
    display: flex;
    flex-direction: column;
    gap: 5px;
    border-radius: 9px;
    padding: 0;
  }

  .ie-filter img,
  .ie-filter-blank {
    display: block;
    width: 100%;
    aspect-ratio: 3 / 2;
    object-fit: cover;
    border-radius: 7px;
    border: 2px solid transparent;
    background: rgba(255, 255, 255, 0.05);
  }

  .ie-filter:hover:not(:disabled) img {
    border-color: rgba(255, 255, 255, 0.25);
  }

  

  .ie-filter-name {
    font-size: 10.5px;
    color: var(--ie-dim);
    text-align: center;
  }

  

  /* size */

  .ie-size {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .ie-size input {
    width: 84px;
    height: 34px;
    padding: 0 10px;
    border: 1px solid var(--ie-line);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.04);
    color: var(--ie-text);
    font: inherit;
    font-variant-numeric: tabular-nums;
    -moz-appearance: textfield;
  }

  .ie-size input::-webkit-outer-spin-button,
  .ie-size input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  

  .ie-size-x,
  .ie-size-unit {
    color: var(--ie-dim);
    font-size: 12px;
  }

  /* footer */

  .ie-foot {
    padding: 16px 20px 18px;
    border-top: 1px solid var(--ie-line);
  }

  

  .ie-save:hover:not(:disabled) {
    background: #66c0f2;
  }

  .ie-busy {
    animation: ie-pulse 1.2s ease-in-out infinite;
  }

  @keyframes ie-pulse {
    50% {
      opacity: 0.65;
    }
  }

  .ie-foot-row {
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
  }

  .ie-quiet {
    padding: 6px 8px;
    border-radius: 7px;
    font-size: 12.5px;
    color: var(--ie-dim);
  }

  .ie-quiet:hover:not(:disabled) {
    color: var(--ie-text);
    background: rgba(255, 255, 255, 0.06);
  }

  @media (prefers-reduced-motion: reduce) {
    .ie-save {
      transition: none;
    }
    .ie-busy {
      animation: none;
    }
  }
</style>
